/**
 * The person's own sandbox in more detail, for the Sandbox page (pages/SandboxHome.jsx), with the person's authorized
 * fetch under the vfs path the web app already uses:
 * - `GET /api/v1/me/sandbox/stats` (the sandbox gateway, vfs-service src/gateway/myStats.js): what the sandbox admin
 *   sees about the person, theirs only -> `{ state, plan: { name, source, limits, overridden }, hours: { thisMonth,
 *   limit, daily: [{ date, activeHours }] }, usage, disk: { code, data: { size, used, target, stores, buckets }, at },
 *   version: { running, current, outdated, runningDiffers }, events: [{ at, type, detail }], projects }`;
 * - `GET /api/v1/me/processes` (the person's own pod, src/sandbox/sandboxProcesses.js): what preview-runner runs ->
 *   `{ available, supported, services: [...], datastores: [...], runner, limits }` or `{ available: false, reason }`.
 * Both resolve to null where the gateway or the pod is too old to know them (404) or vfs-service is not configured.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { shareInflight } from '../utils/sharedRequest';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');

async function getJson(path, what) {
  if (!base()) { return null; }
  const response = await getAuthorizedFetch()(`${base()}${path}`, { headers: { Accept: 'application/json' } });
  if (response.status === 404) { return null; }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `${what} failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

export function getMySandboxStats() {
  return shareInflight('my-sandbox-stats', () => getJson('/api/v1/me/sandbox/stats', 'Sandbox stats'));
}

export function getMyProcesses() {
  return getJson('/api/v1/me/processes', 'Task manager');
}

/**
 * Managing the person's own sandbox (the sandbox gateway, vfs-service src/gateway/mySandbox.js; always the caller's own):
 * - `GET /api/v1/me/sandbox/manage` -> `{ sandboxName, state, pendingDelete, backups: { enabled, keepDays, keepLast } }`,
 *   null where the gateway is too old or not per person (404);
 * - `POST .../restart` -> `{ ok, state: 'restarting' }`; `POST .../sleep` -> `{ ok, state: 'asleep' }`;
 * - `POST .../clear-caches` -> `{ ok, sessions, folders, docker, partial }`;
 * - `POST .../switch-type-now` -> `{ ok, state: 'running' | 'starting', type, from }`: sleep and wake with the chosen
 *   sandbox type (409 `already_running` when it already runs it);
 * - `POST .../delete` `{ deleteDisks?, confirm?, backup? }` -> `{ ok, state: 'missing', keepVolume }`, or (the disks
 *   with a backup first) `{ ok, state: 'deleting', phase: 'backup', backup: { id, keepDays, keepLast } }`.
 * Errors carry the gateway's `status` and `code` (`confirm_mismatch`, `rate_limited`, `deleting`).
 */
const MANAGE = '/api/v1/me/sandbox/manage';

export function getMySandboxManage() {
  return getJson(MANAGE, 'Your sandbox');
}

async function postManage(path, body = {}) {
  if (!base()) { throw new Error('The sandbox is not configured.'); }
  const response = await getAuthorizedFetch()(`${base()}${MANAGE}${path}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `That did not work (${response.status}).`);
    error.status = response.status;
    error.code = data?.code || null;
    throw error;
  }
  return data;
}

export const restartMySandbox = () => postManage('/restart');
export const sleepMySandbox = () => postManage('/sleep');
export const clearMyPreviewCaches = () => postManage('/clear-caches');
export const switchMySandboxTypeNow = () => postManage('/switch-type-now');
export const deleteMySandbox = ({ deleteDisks = false, confirm = '', backup = true } = {}) => (
  postManage('/delete', deleteDisks ? { deleteDisks: true, confirm, backup } : {})
);

/**
 * The person's sandbox type (vfs-service src/gateway/sandboxTemplates/types.js, under `.../manage/type`): the sandbox
 * templates their plan offers, the one the next start runs and the one running now.
 * - `GET` -> `{ plan, current, chosen, default, fallback, running, appliesAtNextStart, types: [{ id, name, description,
 *   default, ready, allowed, current, running, cpu, memoryBytes, requests, diskBytes, dataDiskBytes, docker, stacks,
 *   datastores, tools }], usage }`, null where the gateway is too old (404);
 * - `GET /check?type=` -> `{ type, from, blockers: [{ code, message }], warnings: [{ code, message }] }`;
 * - `PUT` `{ type, confirm }` -> the view again with `changed`. Refused: 403 `not_allowed`, 409 with `blockers`, or 409
 *   `confirm_needed` with `warnings` (without `confirm: true`), 429 `rate_limited`.
 */
const TYPE = `${MANAGE}/type`;

export function getMySandboxType() {
  return getJson(TYPE, 'Your sandbox type');
}

export function checkMySandboxType(type) {
  return getJson(`${TYPE}/check?type=${encodeURIComponent(type)}`, 'Checking the sandbox type');
}

export async function setMySandboxType(type, { confirm = false } = {}) {
  if (!base()) { throw new Error('The sandbox is not configured.'); }
  const response = await getAuthorizedFetch()(`${base()}${TYPE}`, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, confirm }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `That did not work (${response.status}).`);
    error.status = response.status;
    error.code = data?.code || null;
    error.blockers = data?.blockers || [];
    error.warnings = data?.warnings || [];
    throw error;
  }
  return data;
}
